import { useCallback, useEffect, useState } from 'react';

/**
 * Detects whether a scrollable element has overflowing content above and/or below the visible area.
 *
 * @param ref
 * @param threshold
 */
const useScrollIndicator = (ref, threshold = 2) => {
  const [state, setState] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;

    setState({
      canScrollUp: scrollTop > threshold,
      canScrollDown: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, [ref, threshold]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    update();

    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [ref, update]);

  return state;
};

export default useScrollIndicator;
